import { useEffect, useRef, useState } from "react";
import { Globe } from "lucide-react";

/* 🌍 Language switcher — globe button opening a flag dropdown (EN/PT/ES/FR).
   Persistence: localStorage `vts_lang` is the source of truth (survives
   phone cookie wipes); index.html restores the googtrans cookie from it
   before the Google Translate engine loads. */
const LANGS = [
  { code: "pt", flag: "🇧🇷", name: "Português" },
  { code: "en", flag: "🇺🇸", name: "English" },
  { code: "es", flag: "🇪🇸", name: "Español" },
  { code: "fr", flag: "🇫🇷", name: "Français" },
];

const current = () => {
  try {
    const saved = localStorage.getItem("vts_lang");
    if (saved) return saved;
  } catch { /* storage unavailable */ }
  const m = document.cookie.match(/(?:^|;\s*)googtrans=\/en\/([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "en";
};

const setLang = (code) => {
  try { localStorage.setItem("vts_lang", code); } catch { /* storage unavailable */ }
  const host = window.location.hostname;
  ["", `; domain=${host}`, `; domain=.${host}`].forEach((d) => {
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${d}`;
    if (code !== "en") document.cookie = `googtrans=/en/${code}; path=/${d}`;
  });
  // ⚡ live switch through the hidden Google combo when ready (no reload);
  // otherwise reload with the #googtrans hash — the engine reads it with
  // ZERO cookies, which is what phone browsers need.
  const combo = document.querySelector(".goog-te-combo");
  if (combo && code !== "en") {
    combo.value = code;
    combo.dispatchEvent(new Event("change"));
  } else {
    window.location.hash = code === "en" ? "" : `#googtrans(en|${code})`;
    window.location.reload();
  }
};

export const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLangState] = useState(current());
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);

  const pick = (code) => {
    setOpen(false);
    setLangState(code);
    setLang(code);
  };

  return (
    <div ref={boxRef} className="notranslate relative">
      <button
        type="button"
        data-testid="language-switcher"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-vts-line bg-vts-surface/40 hover:bg-vts-surface text-white transition"
        aria-label="Change language"
        title="Change language"
      >
        <Globe className="h-3.5 w-3.5 text-vts-silver/80" />
        <span className="text-[15px] leading-none">{(LANGS.find((l) => l.code === lang) || LANGS[1]).flag}</span>
        <span className="hidden sm:inline text-[11px] font-bold tracking-wide">{lang.toUpperCase().replace("-CN", "")}</span>
      </button>
      {open && (
        <div
          data-testid="language-menu"
          className="absolute right-0 top-full mt-2 z-[70] min-w-[180px] rounded-xl border border-vts-line bg-black/95 backdrop-blur-xl shadow-2xl overflow-hidden py-1"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`language-option-${l.code}`}
              onClick={() => pick(l.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-[14px] transition-colors ${
                lang === l.code ? "bg-vts-blue/15 text-white font-semibold" : "text-white/80 hover:bg-white/5"
              }`}
            >
              <span className="text-[18px] leading-none">{l.flag}</span>
              {l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
